import { Scale, Save, RotateCcw } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { fmt } from "@/lib/feedModels";

export default function CalculatorPanel({
  totalWeight,
  setTotalWeight,
  modelId,
  setModelId,
  models = [],
  name,
  setName,
  result,
  onSave,
  onReset,
  saving,
  zones = [],
  zoneSel = "",
  onZoneSelect,
  zoneStats,
}) {
  return (
    <div
      data-testid="calculator-panel"
      className="lg:col-span-5 bg-white p-6 md:p-8 rounded-2xl shadow-sm border border-[#E0A988]/40 space-y-7"
    >
      <div>
        <h2 className="font-display text-lg md:text-xl font-bold tracking-tight text-[#8A5230]">
          Kalkulator Pakan
        </h2>
        <p className="text-xs md:text-sm text-[#A06E4D] mt-1">
          Masukkan total bobot sapi dan pilih model pakan.
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="input-name" className="text-sm font-semibold text-[#7A4A28]">
          Nama Sapi / Kandang <span className="font-normal text-[#C99A6A]">(opsional)</span>
        </Label>
        <Input
          id="input-name"
          data-testid="input-name"
          type="text"
          placeholder="Contoh: Kandang A — Sapi Limosin"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="h-12 bg-[#FBF3E8] border-[#E0A988]/60 focus-visible:ring-[#A06E4D]"
        />
      </div>

      {zones.length > 0 && (
        <div className="space-y-2">
          <Label className="text-sm font-semibold text-[#7A4A28]">
            Zona Sapi <span className="font-normal text-[#C99A6A]">(opsional — bobot terisi otomatis)</span>
          </Label>
          <Select value={zoneSel} onValueChange={onZoneSelect}>
            <SelectTrigger
              data-testid="select-zone"
              className="h-12 bg-[#FBF3E8] border-[#E0A988]/60 focus:ring-[#A06E4D]"
            >
              <SelectValue placeholder="Pilih zona sapi" />
            </SelectTrigger>
            <SelectContent>
              {zones.map((z) => (
                <SelectItem key={z} value={z} data-testid={`select-zone-option-${z}`}>
                  {z}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {zoneStats && (
            <p data-testid="zone-summary" className="text-xs text-[#A06E4D]">
              {zoneStats.count} sapi · Total bobot {fmt(zoneStats.total)} kg
            </p>
          )}
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="input-total-weight" className="text-sm font-semibold text-[#7A4A28]">
          Total Bobot Sapi
        </Label><div className="relative">
          <Input
            id="input-total-weight"
            data-testid="input-total-weight"
            type="number"
            min="0"
            step="any"
            placeholder="Contoh: 2000"
            value={totalWeight}
            onChange={(e) => setTotalWeight(e.target.value)}
            className="h-12 pr-12 text-lg font-mono-num bg-[#FBF3E8] border-[#E0A988]/60 focus-visible:ring-[#A06E4D]"
          />
          <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm font-semibold text-[#C99A6A]">
            kg
          </span>
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-sm font-semibold text-[#7A4A28]">Model Pakan</Label>
        <Select value={modelId} onValueChange={setModelId}>
          <SelectTrigger
            data-testid="select-feed-model"
            className="h-12 bg-[#FBF3E8] border-[#E0A988]/60 focus:ring-[#A06E4D]"
          >
            <SelectValue placeholder="Pilih model pakan" />
          </SelectTrigger>
          <SelectContent>
            {models.map((m) => (
              <SelectItem
                key={m.id}
                value={m.id}
                data-testid={`select-option-${m.id}`}
              >
                {m.label} — Comboran : {m.type === "silase" ? "Silase" : "Onggok"} ({Math.round(m.comboran * 100)}% : {Math.round(m.hijauan * 100)}%)
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-xl bg-[#8A5230] text-white p-5 space-y-1">
        <div className="flex items-center gap-2 text-[#F7E5D2] text-xs font-semibold uppercase tracking-wider">
          <Scale className="w-4 h-4" />
          Kebutuhan Pakan Harian (10% bobot)
        </div>
        <div
          data-testid="daily-feed-requirement"
          className="text-3xl md:text-4xl font-mono-num font-bold tracking-tight"
        >
          {result ? `${fmt(result.totalFeed)} kg` : "—"}
        </div>
        {result && (
          <p className="text-xs text-[#F7E5D2]/70">
            dari total bobot {fmt(result.totalWeight)} kg
          </p>
        )}
      </div>

      <div className="flex gap-3">
        <Button
          data-testid="btn-save-calculation"
          onClick={onSave}
          disabled={!result || saving}
          className="flex-1 h-12 rounded-full bg-[#A06E4D] hover:bg-[#8A5230] text-white font-semibold transition-colors"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? "Menyimpan..." : "Simpan Perhitungan"}
        </Button>
        <Button
          data-testid="btn-reset-form"
          variant="outline"
          onClick={onReset}
          className="h-12 w-12 rounded-full border-[#E0A988]/70 text-[#A06E4D] hover:bg-[#F7E5D2]"
        >
          <RotateCcw className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}
